import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FlatTreeControl, CdkTreeModule } from '@angular/cdk/tree';
import { ArrayDataSource } from '@angular/cdk/collections';

// ── Clé localStorage — même pattern que les checkboxes dans area.component ───
const LS_SELECTED_NODE_ID = 'area.selectedNodeId';

// Format stocké : "type:id" (ex: "region:23", "site:58")
// Cela évite la collision entre un site et une région ayant le même id numérique.
function lsLoadSelectedKey(): string | null {
  try { return localStorage.getItem(LS_SELECTED_NODE_ID); } catch { return null; }
}

function buildKey(type: string | undefined, id: any): string {
  return `${type ?? ''}:${id}`;
}

// ─── Public data model ───────────────────────────────────────────────────────

export interface TreeNode {
  id?:       any;
  type?:     string;
  label:     string;
  badge?:    string;
  checked?:  boolean;
  children?: TreeNode[];
}

export interface TreeNodeEvent {
  id:       any;
  type:     string | undefined;
  node:     TreeNode;
  action:   'node-click' | 'badge-click' | 'checkbox-change';
  checked?: boolean;
}

// ─── Internal flat node ──────────────────────────────────────────────────────

export interface FlatNode {
  id:         any;
  type?:      string;
  label:      string;
  badge?:     string;
  checked:    boolean;
  level:      number;
  expandable: boolean;
  source:     TreeNode;
  index:      number;
}

// ─── Flatten helper ──────────────────────────────────────────────────────────

function flattenTree(nodes: TreeNode[], level = 0, result: FlatNode[] = []): FlatNode[] {
  for (const node of nodes) {
    const flat: FlatNode = {
      id:         node.id,
      type:       node.type,
      label:      node.label,
      badge:      node.badge,
      checked:    node.checked !== false,
      level,
      expandable: !!node.children?.length,
      source:     node,
      index:      result.length,
    };
    result.push(flat);
    if (node.children?.length) {
      flattenTree(node.children, level + 1, result);
    }
  }
  return result;
}

@Component({
  selector: 'app-tree-view',
  standalone: true,
  imports: [CdkTreeModule, TranslateModule],
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class TreeViewComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() data: TreeNode[] = [];
  @Input() labelEllipsis: 'start' | 'end' = 'end';

  /** Quand false, seul le nœud cliqué est mis en surbrillance — ses parents ne sont pas sélectionnés. */
  @Input() selectParent = true;

  @Input() showSelectAll = true;

  /** Sélectionne un nœud par id sans reconstruire le tree —
   *  écrit dans localStorage puis appelle _applyExternalSelection(). */
  @Input() set selectedId(id: string | null | undefined) {
    if (id == null) return;
    try { localStorage.setItem(LS_SELECTED_NODE_ID, id); } catch {}
    this._applyExternalSelection();
  }

  @Input() set searchText(value: string) {
    const next = (value || '').trim().toLowerCase();
    if (next === this._searchText) return;
    this._searchText = next;
    if (this._flatNodes.length) this._applyFilter();
  }

  @Output() nodeClick      = new EventEmitter<TreeNodeEvent>();
  @Output() badgeClick     = new EventEmitter<TreeNodeEvent>();
  @Output() checkboxChange = new EventEmitter<TreeNodeEvent>();
  @Output() selectAllChange = new EventEmitter<boolean>();

  @ViewChild('scrollContent') scrollContentRef!: ElementRef<HTMLElement>;
  @ViewChild('scrollTrack')   scrollTrackRef!:   ElementRef<HTMLElement>;

  private _cdr        = inject(ChangeDetectorRef);
  private _searchText = '';
  private _flatNodes: FlatNode[] = [];

  private _renderSet = new Set<FlatNode>();

  treeControl = new FlatTreeControl<FlatNode>(
    (n) => n.level,
    (n) => n.expandable,
  );
  dataSource = new ArrayDataSource<FlatNode>([]);

  // ─── Scrollbar ────────────────────────────────────────────────────────────
  thumbHeight   = 0;
  thumbTop      = 0;
  showScrollbar = false;

  private _isDragging         = false;
  private _dragStartY         = 0;
  private _dragStartScrollTop = 0;
  private _resizeObserver!: ResizeObserver;
  private _onMouseMove = this._handleMouseMove.bind(this);
  private _onMouseUp   = this._handleMouseUp.bind(this);

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this._flatNodes = flattenTree(this.data);
      this.treeControl = new FlatTreeControl<FlatNode>(
        (n) => n.level,
        (n) => n.expandable,
      );
      this.treeControl.dataNodes = this._flatNodes;
      this.dataSource = new ArrayDataSource(this._flatNodes);
      this._applyFilter();
      // Restaure la sélection visuelle depuis localStorage
      // (même pattern que les checkboxes : lecture directe, sans input ni signal)
      this._applyExternalSelection();
      requestAnimationFrame(() => requestAnimationFrame(() => this._computeScrollbar()));
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => this._computeScrollbar()));
    this._resizeObserver = new ResizeObserver(() => this._computeScrollbar());
    this._resizeObserver.observe(this.scrollContentRef.nativeElement);
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
    this._tooltipEl?.remove();
    this._tooltipEl = null;
  }

  // ─── Filtre ───────────────────────────────────────────────────────────────

  private _applyFilter(): void {
    this._renderSet.clear();
    this.treeControl.collapseAll();

    if (!this._searchText) {
      for (const n of this._flatNodes) {
        this._renderSet.add(n);
      }
      this._flatNodes
        .filter(n => n.level === 0 && n.expandable)
        .forEach(n => this.treeControl.expand(n));
      this._cdr.detectChanges();
      requestAnimationFrame(() => this._computeScrollbar());
      return;
    }

    const parentOf = new Array<FlatNode | null>(this._flatNodes.length).fill(null);
    for (let i = 1; i < this._flatNodes.length; i++) {
      const node = this._flatNodes[i];
      for (let j = i - 1; j >= 0; j--) {
        if (this._flatNodes[j].level === node.level - 1) {
          parentOf[i] = this._flatNodes[j];
          break;
        }
      }
    }

    for (const node of this._flatNodes) {
      if (!node.label.toLowerCase().includes(this._searchText)) continue;

      this._renderSet.add(node);

      if (node.expandable) {
        this.treeControl.expand(node);
        for (let i = node.index + 1; i < this._flatNodes.length; i++) {
          if (this._flatNodes[i].level <= node.level) break;
          this._renderSet.add(this._flatNodes[i]);
        }
      }

      let ancestor = parentOf[node.index];
      while (ancestor) {
        this._renderSet.add(ancestor);
        this.treeControl.expand(ancestor);
        ancestor = parentOf[ancestor.index];
      }
    }

    this._cdr.detectChanges();
    requestAnimationFrame(() => this._computeScrollbar());
  }

  // ─── shouldRender ─────────────────────────────────────────────────────────

  shouldRender(node: FlatNode): boolean {
    if (!this._renderSet.has(node)) return false;
    let parent = this._getParent(node);
    while (parent) {
      if (!this.treeControl.isExpanded(parent)) return false;
      parent = this._getParent(parent);
    }
    return true;
  }

  // ─── Sélection externe (restauration depuis localStorage) ─────────────────
  // Lit l'id sauvegardé, trouve le FlatNode, ouvre ses parents,
  // et applique la sélection visuelle. Aucun événement émis.
  private _applyExternalSelection(): void {
    const savedKey = lsLoadSelectedKey();
    if (!this._flatNodes.length) return;

    // Empty key → clear selection
    if (!savedKey) {
      this.selectedNode = null;
      this._selectedAncestors.clear();
      this._cdr.detectChanges();
      return;
    }

    // Format "type:id" → match précis sur les deux critères
    // Format legacy "id" seul → fallback sur l'id uniquement
    let node: FlatNode | undefined;
    if (savedKey.includes(':')) {
      const colonIdx  = savedKey.indexOf(':');
      const savedType = savedKey.slice(0, colonIdx);
      const savedId   = savedKey.slice(colonIdx + 1);
      node = this._flatNodes.find(n => String(n.id) === savedId && (n.type ?? '') === savedType);
    } else {
      node = this._flatNodes.find(n => String(n.id) === savedKey);
    }
    if (!node) return;

    // Ouvre tous les parents pour que le nœud soit visible dans le tree
    let parent = this._getParent(node);
    while (parent) {
      this.treeControl.expand(parent);
      parent = this._getParent(parent);
    }

    // Applique selectedNode + _selectedAncestors (même logique que selectNode)
    this.selectedNode = node;
    this._selectedAncestors.clear();
    if (this.selectParent) {
      let ancestor = this._getParent(node);
      while (ancestor) {
        this._selectedAncestors.add(ancestor);
        ancestor = this._getParent(ancestor);
      }
    }
    this._cdr.detectChanges();

    // Scroll vers le nœud — on utilise la clé complète "type:id" pour cibler
    // le bon nœud DOM via data-node-key (évite de scroller vers le mauvais nœud
    // quand un site et une région partagent le même id numérique)
    requestAnimationFrame(() => this._scrollToSelected(savedKey));
  }

  // Même pattern que scrollToHighlighted dans data-table.component :
  // trouve l'élément via data-node-id et centre le conteneur scrollable dessus.
  private _scrollToSelected(key: string): void {
    const container = this.scrollContentRef?.nativeElement as HTMLElement | undefined;
    if (!container) return;

    // Cherche d'abord via data-node-key (format "type:id", sans ambiguïté),
    // puis fallback sur data-node-id (id pur) pour la compatibilité
    const nodeEl =
      container.querySelector<HTMLElement>(`[data-node-key="${key}"]`) ??
      container.querySelector<HTMLElement>(`[data-node-id="${key}"]`);
    if (!nodeEl) return;

    const containerRect = container.getBoundingClientRect();
    const nodeRect      = nodeEl.getBoundingClientRect();
    const nodeOffsetTop = nodeRect.top - containerRect.top + container.scrollTop;
    const scrollTop     = nodeOffsetTop - container.clientHeight / 2 + nodeEl.offsetHeight / 2;

    container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
  }

  // ─── Scrollbar ────────────────────────────────────────────────────────────

  private _computeScrollbar(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) return;
    const needsScroll = content.scrollHeight > content.clientHeight;
    if (needsScroll !== this.showScrollbar) {
      this.showScrollbar = needsScroll;
      this._cdr.detectChanges();
    }
    if (this.showScrollbar) requestAnimationFrame(() => this._computeThumb());
  }

  private _computeThumb(): void {
    const content = this.scrollContentRef?.nativeElement;
    const track   = this.scrollTrackRef?.nativeElement;
    if (!content || !track) return;
    this.thumbHeight = Math.max(32, (content.clientHeight / content.scrollHeight) * track.clientHeight);
    this._updateThumbPosition();
    this._cdr.detectChanges();
  }

  private _updateThumbPosition(): void {
    const content = this.scrollContentRef?.nativeElement;
    const track   = this.scrollTrackRef?.nativeElement;
    if (!content || !track) return;
    const maxScroll   = content.scrollHeight - content.clientHeight;
    const scrollRatio = maxScroll > 0 ? content.scrollTop / maxScroll : 0;
    this.thumbTop     = scrollRatio * (track.clientHeight - this.thumbHeight);
  }

  onScroll(): void { this._updateThumbPosition(); }

  onTrackClick(event: MouseEvent): void {
    const track   = this.scrollTrackRef.nativeElement;
    const content = this.scrollContentRef.nativeElement;
    const clickY  = event.clientY - track.getBoundingClientRect().top;
    content.scrollTop = (clickY / track.clientHeight) * (content.scrollHeight - content.clientHeight);
    this._updateThumbPosition();
  }

  onThumbMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._isDragging         = true;
    this._dragStartY         = event.clientY;
    this._dragStartScrollTop = this.scrollContentRef.nativeElement.scrollTop;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup',   this._onMouseUp);
  }

  private _handleMouseMove(event: MouseEvent): void {
    if (!this._isDragging) return;
    const content = this.scrollContentRef.nativeElement;
    const track   = this.scrollTrackRef.nativeElement;
    const deltaY  = event.clientY - this._dragStartY;
    const ratio   = (content.scrollHeight - content.clientHeight) / (track.clientHeight - this.thumbHeight);
    content.scrollTop = this._dragStartScrollTop + deltaY * ratio;
    this._updateThumbPosition();
  }

  private _handleMouseUp(): void {
    this._isDragging = false;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }

  // ─── Tree events ──────────────────────────────────────────────────────────

  onNodeClick(node: FlatNode, event: Event): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('node-checkbox') || target.closest('.node-badge')) return;
    this.selectNode(node, event);
    this.nodeClick.emit({ id: node.id, type: node.type, node: node.source, action: 'node-click' });
  }

  private _tooltipEl: HTMLElement | null = null;

  onLabelEnter(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    if (el.scrollWidth <= el.clientWidth) return;

    const tip = this._ensureTooltip();
    tip.textContent = (el.textContent ?? '').trim();
    tip.classList.remove('tree-node-tooltip--visible');

    const rect = el.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();

    let top = rect.top - tipRect.height - 6;
    if (top < 4) top = rect.bottom + 6;

    let left = rect.left;
    const maxLeft = window.innerWidth - tipRect.width - 8;
    if (left > maxLeft) left = Math.max(8, maxLeft);

    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
    requestAnimationFrame(() => {
      this._tooltipEl?.classList.add('tree-node-tooltip--visible');
    });
  }

  onLabelLeave(): void {
    this._tooltipEl?.classList.remove('tree-node-tooltip--visible');
  }

  private _ensureTooltip(): HTMLElement {
    if (this._tooltipEl) return this._tooltipEl;
    const el = document.createElement('div');
    el.className = 'tree-node-tooltip';
    document.body.appendChild(el);
    this._tooltipEl = el;
    return el;
  }

  onBadgeClick(node: FlatNode, event: Event): void {
    event.stopPropagation();
    this.badgeClick.emit({ id: node.id, type: node.type, node: node.source, action: 'badge-click' });
  }

  toggleChecked(node: FlatNode, event: Event): void {
    event.stopPropagation();
    node.checked = !node.checked;
    node.source.checked = node.checked;

    // Propagation descendante
    if (node.expandable) {
      this._getDescendants(node).forEach(child => {
        child.checked = node.checked;
        child.source.checked = node.checked;
      });
    }

    // Propagation ascendante : cocher un nœud coche son parent
    if (node.checked) {
      this._checkParentsIfNeeded(node);
    }

    this.checkboxChange.emit({
      id:      node.id,
      type:    node.type,
      node:    node.source,
      action:  'checkbox-change',
      checked: node.checked,
    });
    requestAnimationFrame(() => this._computeScrollbar());
  }

  get allChecked(): boolean {
    return this._flatNodes.length > 0 && this._flatNodes.every(n => n.checked);
  }

  get someChecked(): boolean {
    return this._flatNodes.some(n => n.checked);
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    for (const n of this._flatNodes) {
      n.checked        = checked;
      n.source.checked = checked;
    }
    this.selectAllChange.emit(checked);
    this._cdr.detectChanges();
    requestAnimationFrame(() => this._computeScrollbar());
  }

  private _checkParentsIfNeeded(node: FlatNode): void {
    let parent = this._getParent(node);
    while (parent) {
      parent.checked        = true;
      parent.source.checked = true;
      parent = this._getParent(parent);
    }
  }

  // ─── Tree helpers ─────────────────────────────────────────────────────────

  private _getParent(node: FlatNode): FlatNode | null {
    for (let i = node.index - 1; i >= 0; i--) {
      if (this._flatNodes[i].level === node.level - 1) return this._flatNodes[i];
    }
    return null;
  }

  private _getDescendants(node: FlatNode): FlatNode[] {
    const result: FlatNode[] = [];
    for (let i = node.index + 1; i < this._flatNodes.length; i++) {
      if (this._flatNodes[i].level > node.level) result.push(this._flatNodes[i]);
      else break;
    }
    return result;
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;
  isExpanded(node: FlatNode): boolean { return this.treeControl.isExpanded(node); }

  onToggle(): void {
    requestAnimationFrame(() => this._computeScrollbar());
  }

  selectedNode: FlatNode | null = null;
  private _selectedAncestors = new Set<FlatNode>();

  selectNode(node: FlatNode, event: Event): void {
    if ((event.target as HTMLElement).classList.contains('node-checkbox')) return;
    this.selectedNode = node;
    this._selectedAncestors.clear();
    if (this.selectParent) {
      let parent = this._getParent(node);
      while (parent) {
        this._selectedAncestors.add(parent);
        parent = this._getParent(parent);
      }
    }
    // Persiste "type:id" pour éviter la collision entre types ayant le même id numérique
    try { localStorage.setItem(LS_SELECTED_NODE_ID, buildKey(node.type, node.id)); } catch {}
  }

  isSelected(node: FlatNode): boolean {
    return this.selectedNode === node || this._selectedAncestors.has(node);
  }

  getIndent(level: number): string {
    return level === 0 ? '0px' : `${level * 20}px`;
  }

  getLevelArray(level: number): number[] {
    return Array.from({ length: level }, (_, i) => i);
  }

  getGuideIndices(node: FlatNode): number[] {
    return Array.from({ length: node.level }, (_, i) => i);
  }
}